import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Wand2, Zap, ShieldCheck, Play, ArrowRight, Clapperboard } from "lucide-react";

const features = [
  { icon: Wand2, title: "Prompts en français", desc: "Décrivez votre scène en une phrase : l'IA s'occupe du reste." },
  { icon: Zap, title: "Résultats rapides", desc: "Kling Turbo et Veo 3.1 Fast génèrent vos clips en quelques minutes." },
  { icon: Clapperboard, title: "Formats verticaux & cinéma", desc: "16:9 pour YouTube, 9:16 pour TikTok, Reels et Shorts." },
  { icon: ShieldCheck, title: "Paiements sécurisés", desc: "Stripe et Revolut Pay — carte, Apple Pay, Google Pay et plus." },
];

const Landing = () => {
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`)
      .then((r) => r.json())
      .then(setGallery)
      .catch(() => {});
  }, []);

  return (
    <div className="noise relative overflow-hidden">
      <section className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#8B5CF6]/25 blur-[120px] pointer-events-none" />
        <div className="absolute -top-20 right-0 w-80 h-80 rounded-full bg-[#EC4899]/20 blur-[120px] pointer-events-none" />
        <div className="relative stagger-in max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#EC4899] border border-[#EC4899]/30 rounded-full px-4 py-1.5" data-testid="landing-badge">
            <Sparkles className="w-3.5 h-3.5" /> Génération vidéo par IA
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight" data-testid="landing-title">
            Vos idées deviennent des <span className="text-gradient">vidéos IA</span> en quelques minutes.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl" data-testid="landing-subtitle">
            Décrivez une scène, choisissez un format, et laissez LumenAI Studio générer un clip cinématographique prêt à publier.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button asChild size="lg" className="pulse-glow bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white border-0 text-base px-8" data-testid="landing-cta-generate">
              <Link to={localStorage.getItem("token_seen") ? "/studio" : "/inscription"}>
                Créer ma première vidéo <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-border text-base px-8" data-testid="landing-cta-pricing">
              <Link to="/tarifs">Voir les tarifs</Link>
            </Button>
          </div>
          <p className="mt-4 text-sm text-muted-foreground" data-testid="landing-free-note">
            5 crédits offerts à l'inscription — sans carte bancaire.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 border-t border-border/60">
        <h2 className="text-2xl sm:text-3xl font-bold" data-testid="landing-features-title">Tout ce qu'il faut pour créer</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-in">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-6 hover:border-[#8B5CF6]/50 transition-colors" data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className="w-6 h-6 text-[#8B5CF6]" />
              <h3 className="mt-4 font-display text-base font-medium">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {gallery.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 border-t border-border/60">
          <h2 className="text-2xl sm:text-3xl font-bold" data-testid="landing-gallery-title">Créations de la communauté</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.slice(0, 6).map((v) => (
              <div key={v.id} className="rounded-2xl border border-border overflow-hidden bg-card group" data-testid={`landing-gallery-item-${v.id}`}>
                <div className="relative aspect-video bg-black/50">
                  <video src={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${v.id}/file`} controls preload="metadata" className="w-full h-full object-cover" data-testid="gallery-video-player" />
                </div>
                <p className="p-4 text-sm text-muted-foreground line-clamp-2">{v.prompt}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 border-t border-border/60">
        <div className="relative rounded-3xl border border-[#8B5CF6]/30 bg-gradient-to-br from-[#8B5CF6]/10 to-[#EC4899]/10 p-10 sm:p-14 text-center glow-purple" data-testid="landing-final-cta">
          <Play className="w-10 h-10 mx-auto text-[#EC4899]" />
          <h2 className="mt-6 text-2xl sm:text-4xl font-bold">Prêt à tourner votre première vidéo ?</h2>
          <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
            Abonnements mensuels ou annuels à partir de 9,99 €/mois. Annuel : 2 mois offerts.
          </p>
          <Button asChild size="lg" className="mt-8 bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white border-0 px-10" data-testid="landing-final-cta-button">
            <Link to="/tarifs">Choisir mon offre</Link>
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground" data-testid="landing-footer">
        © {new Date().getFullYear()} LumenAI Studio — Paiements sécurisés Stripe & Revolut · Revenus versés sur Revolut
      </footer>
    </div>
  );
};

export default Landing;
